<template>
  <div class="page-content-wrapper m-h-90">
    <!-- Top Stats Cards -->
    <div class="col-sm-12">
      <div class="row g-3">
        <div class="col-sm-12 col-md-4">
          <icon-card title="Customers" :count="total_project_customers" icon_class="fa fa-users"/>
        </div>
        <div class="col-sm-12 col-md-4">
          <icon-card title="Active Workflows" :count="active_workflows" icon_class="fas fa-project-diagram"/>
        </div>
        <div class="col-sm-12 col-md-4">
          <icon-card title="Admins" :count="admins" icon_class="fas fa-user-shield"/>
        </div>
      </div>

      <!-- Workflow Table -->
      <div v-if="loading" class="text-center mt-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else>
        <div class="card rounded-3 shadow mt-4 border-1">
          <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center">
            <p class="text-s fw-bold">Your Workflows</p>
            <div class="d-flex gap-2">
              <button
                v-if="role === 'ClientAdmin' || (role === 'Client' && can_create_workflow)"
                type="button"
                class="btn btn-dark rounded-3 fw-bold shadow btn-sm text-xxs"
                @click="view_workflow_templates()"
              >
                <i class="fas fa-file-import"></i> Add From Template
              </button>
              <button
                v-if="role === 'ClientAdmin' || (role === 'Client' && can_create_workflow)"
                type="button"
                class="btn btn-dark rounded-3 fw-bold shadow btn-sm text-xxs"
                data-bs-toggle="modal"
                data-bs-target="#create_workflow"
              >
                <i class="fas fa-plus"></i> New Workflow
              </button>
            </div>
          </div>

          <div class="card-body">
            <CustomTable
              :columns="columns"
              :fetchData="getWorkflows"
              :hasActions="true"
              ref="workflowTable"
              tableKey="client_workflows"
            >
              <template v-slot:cell-date_created="{ row }">
                {{ formatDate(row.date_created) }}
              </template>

              <template v-slot:cell-workflow_status="{ row }">
                <span
                  v-if="row"
                  class="text-xxs"
                  :class="[
                    'badge border text-xxs',
                    get_status(row.workflow_status),
                  ]"
                >
                  {{ row.workflow_status }}
                </span>
              </template>

              <template v-slot:cell-workflow_state="{ row }">
                <div v-if="row">
                  <div
                    v-if="role === 'ClientAdmin' || (role === 'Client' && can_create_workflow)"
                    class="form-check form-switch"
                  >
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="row.is_active"
                      @change="
                        toggle_workflow_status(
                          row.workflow_id,
                          $event.target.checked
                        )
                      "
                      :disabled="
                        ['pending', 'declined', 'incomplete'].includes(
                          row.workflow_status
                        )
                      "
                    />
                    <label
                      class="form-check-label text-xxs"
                      :for="'workflow-switch' + row.workflow_id"
                    >
                      {{ row.is_active ? "Active" : "Inactive" }}
                    </label>
                  </div>
                  <div v-else>
                    <span class="text-xxs">
                      {{ row.is_active ? "Active" : "Inactive" }}
                    </span>
                  </div>
                </div>
              </template>

              <template v-slot:actions="{ row }">
                <div v-if="row" class="d-flex align-items-center">
                  <button @click="view_workflow(row.workflow_id)" class="btn btn-sm btn-bg-green rounded-3 shadow-sm text-xs me-2" title="View Customers">
                    <i class="fas fa-users"></i> Customers
                  </button>
                  <button v-if="role === 'ClientAdmin' || (role === 'Client' && can_create_workflow)" @click="edit_workflow(row.workflow_id || row.id)" class="btn btn-sm btn-bg-dark rounded-3 shadow-sm text-xs me-2" title="Manage Workflow">
                    <i class="fas fa-edit"></i> Manage
                  </button>
                  <div class="custom-popup" :class="{ 'active': activePopupWorkflowId === row.workflow_id }">
                    <div class="custom-burger" tabindex="0" @click="togglePopup(row.workflow_id)">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <nav class="custom-popup-window">
                      <legend>Actions</legend>
                      <ul>
                        <li>
                          <button @click="update_complete_notification(row.workflow_id, !row.workflow.complete_notification)" :disabled="['pending', 'declined', 'incomplete'].includes(row.workflow_status)">
                            <i class="fas fa-bell me-2"></i>
                            Notify Upon Completion
                            <i class="fas fa-check text-success ms-2" v-if="row.workflow.complete_notification"></i>
                          </button>
                        </li>
                        <li>
                          <button 
                            v-if="!row.disableAnonymousClient"
                            @click="open_anonymous_client_modal(row.workflow_id)"
                            :disabled="['pending', 'declined', 'incomplete'].includes(row.workflow_status) || !row.is_active"
                          >
                            <i class="fas fa-user-secret me-2"></i>
                            Anonymous Client
                          </button>
                          <button v-else disabled class="disabled">
                            <i class="fas fa-user-secret me-2"></i>
                            Anonymous Client
                          </button>
                        </li>
                        <li>
                          <button 
                            @click="behalf_application(row.workflow_id)"
                            :disabled="['pending', 'declined', 'incomplete'].includes(row.workflow_status) || !row.is_active || row.workflow_type == 'standard'"
                            :class="{'disabled': ['pending', 'declined', 'incomplete'].includes(row.workflow_status) || !row.is_active || row.workflow_type == 'standard'}"
                          >
                            <i class="fas fa-file-alt me-2"></i>
                            Behalf Application
                          </button>
                        </li>
                        <li>
                          <button 
                            @click="batch_processing(row.workflow_id)"
                            :disabled="['pending', 'declined', 'incomplete'].includes(row.workflow_status) || !row.is_active || row.allow_batch_onboarding == 'False'"
                            :class="{'disabled': ['pending', 'declined', 'incomplete'].includes(row.workflow_status) || !row.is_active || row.allow_batch_onboarding == 'False'}"
                          >
                            <i class="fas fa-file-alt me-2"></i>
                            Batch Processing
                          </button>
                        </li>
                        <li>
                          <button @click="view_batches(row.workflow_id)">
                            <i class="fas fa-table me-2"></i>
                            View Batches
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </template>
            </CustomTable>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Create Workflow Modal -->
  <div
    class="modal fade"
    id="create_workflow"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
    aria-labelledby="create_workflow_label"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content bg-white rounded-3">
        <div class="modal-header border-bottom-0">
          <h5 class="modal-title text-s fw-bold" id="create_workflow_label">
            Create Workflow
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <div class="form-group mb-3">
            <input
              type="text"
              v-model="workflow_name"
              class="form-control text-xs rounded-small p-3 shadow-sm"
              placeholder="Workflow Name"
            />
            <small
              class="text-danger p-3 text-xxs fw-bold d-none workflow-name-messages"
              >Please enter a workflow name</small
            >
            <small
              class="text-danger p-2 text-xxs fw-bold d-none error-messages"
            ></small>
          </div>
          <div class="form-group mb-3">
            <textarea
              v-model="workflow_description"
              class="form-control h-100 text-xs rounded-small shadow-sm"
              placeholder="Workflow Description"
              rows="5"
            ></textarea>
            <small
              class="text-danger p-2 text-xxs fw-bold d-none workflow-description-messages"
              >Please enter a workflow description</small
            >
          </div>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <label class="form-check-label text-xxs mb-0" for="multiple-onboarding-toggle">
              Allow Multiple onboarding
            </label>
            <div class="form-check form-switch m-0">
              <input
          class="form-check-input"
          type="checkbox"
          id="multiple-onboarding-toggle"
          v-model="multiple_onboarding"
              />
            </div>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <label class="form-check-label text-xxs mb-0" for="store-on-api-toggle">
              Store on API
            </label>
            <div class="form-check form-switch m-0">
              <input
          class="form-check-input"
          type="checkbox"
          id="store-on-api-toggle"
          v-model="store_on_api"
              />
            </div>
          </div>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <label class="form-check-label text-xxs mb-0" for="batch-onboarding-toggle">
              Allow customer to do batch upload
            </label>
            <div class="form-check form-switch m-0">
              <input
          class="form-check-input"
          type="checkbox"
          id="batch-onboarding-toggle"
          v-model="batch_onboarding"
              />
            </div>
          </div>
          <button
            type="button"
            class="btn bg-green float-end shadow text-xs fw-bold"
            @click="create_workflow()"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Anonymous Client Modal -->
  <div
    class="modal fade"
    id="anonymous_client_modal"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
    aria-labelledby="anonymous_client_label"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content bg-white rounded-3">
        <div class="modal-header border-bottom-0">
          <h5 class="modal-title text-s fw-bold" id="anonymous_client_label">
            Anonymous Client
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <!-- Nav tabs -->
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item" role="presentation">
              <button
                class="nav-link active text-xs text-dark"
                id="email-tab"
                data-bs-toggle="tab"
                data-bs-target="#email-tab-pane"
                type="button"
                role="tab"
                aria-controls="email-tab-pane"
                aria-selected="true"
              >
                Email Invite
              </button>
            </li>
            <li class="nav-item d-none" role="presentation">
              <button
                class="nav-link text-xs text-dark"
                id="sms-tab"
                data-bs-toggle="tab"
                data-bs-target="#sms-tab-pane"
                type="button"
                role="tab"
                aria-controls="sms-tab-pane"
                aria-selected="false"
              >
                SMS Invite
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button
                class="nav-link text-xs text-dark"
                id="qr-tab"
                data-bs-toggle="tab"
                data-bs-target="#qr-tab-pane"
                type="button"
                role="tab"
                aria-controls="qr-tab-pane"
                aria-selected="false"
              >
                QR Code
              </button>
            </li>
          </ul>

          <!-- Tab content -->
          <div class="tab-content mt-3">
            <!-- Email Tab -->
            <div
              class="tab-pane fade show active"
              id="email-tab-pane"
              role="tabpanel"
              aria-labelledby="email-tab"
              tabindex="0"
            >
              <div class="form-group mb-3">
                <label class="form-label">Email Address</label>
                <input
                  type="email"
                  v-model="anonymous_email"
                  class="form-control text-xs rounded-small p-3 shadow-sm"
                  placeholder="Enter email address"
                />
                <small
                  class="text-danger p-2 text-xxs fw-bold d-none email-error"
                ></small>
              </div>
              <button
                type="button"
                class="btn bg-green float-end shadow text-xs fw-bold"
                @click="send_email_invite"
              >
                Send Invite
              </button>
            </div>

            <!-- SMS Tab -->
            <div
              class="tab-pane fade"
              id="sms-tab-pane"
              role="tabpanel"
              aria-labelledby="sms-tab"
              tabindex="0"
            >
              <div class="form-group mb-3">
                <label class="form-label">Phone Number</label>
                <input
                  type="text"
                  v-model="anonymous_phone"
                  class="form-control text-xs rounded-small p-3 shadow-sm"
                  placeholder="Enter phone number"
                />
                <small
                  class="text-danger p-2 text-xxs fw-bold d-none phone-error"
                ></small>
              </div>
              <button
                type="button"
                class="btn bg-green float-end shadow text-xs fw-bold"
                @click="send_sms_invite"
              >
                Send Invite
              </button>
            </div>

            <!-- QR Code Tab -->
            <div
              class="tab-pane fade"
              id="qr-tab-pane"
              role="tabpanel"
              aria-labelledby="qr-tab"
              tabindex="0"
            >
              <div class="text-center mb-3">
                <div v-if="qr_code_loading" class="my-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
                <div v-else-if="qr_code_url" class="my-3">
                  <img
                    :src="
                      'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' +
                      encodeURIComponent(qr_code_url)
                    "
                    alt="QR Code"
                    class="img-fluid"
                  />
                  <div class="mt-3">
                    <p class="mb-1 text-xs">
                      Scan this QR code or use the link below:
                    </p>
                    <div class="input-group mt-2">
                      <input
                        type="text"
                        class="form-control text-xs rounded-none"
                        v-model="qr_code_url"
                        readonly
                      />
                      <button
                        class="btn bg-green ms-2 rounded-2 shadow text-xs fw-bold"
                        type="button"
                        @click="copy_to_clipboard(qr_code_url)"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>


</template>

<script src="./project_dashboard.js"></script>

<style scoped>
/* Custom popup action dropdown - inspired by mockapi.io but adapted to fit current UI */

.custom-popup {
  --burger-line-width: 1.125em;
  --burger-line-height: 0.125em;
  --burger-offset: 0.625em;
  --burger-bg: rgba(0, 0, 0, .1);
  --burger-color: #495057;
  --burger-line-border-radius: 0.1875em;
  --burger-diameter: 2.125em;
  --burger-btn-border-radius: calc(var(--burger-diameter) / 2);
  --burger-line-transition: .3s;
  --burger-transition: all .1s ease-in-out;
  --burger-hover-scale: 1.05;
  --burger-active-scale: .95;
  --burger-enable-outline-color: var(--burger-bg);
  --burger-enable-outline-width: 0.125em;
  --burger-enable-outline-offset: var(--burger-enable-outline-width);
  /* nav */
  --nav-padding-x: 0.75em;
  --nav-padding-y: 1em;
  --nav-border-radius: 0.75em;
  --nav-border-color: #e9ecef;
  --nav-border-width: 1px;
  --nav-shadow-color: rgba(0, 0, 0, .1);
  --nav-shadow-width: 0 0.25rem 0.5rem;
  --nav-bg: #ffffff;
  --nav-font-family: inherit;
  --nav-default-scale: .95;
  --nav-active-scale: 1;
  --nav-position-left: unset;
  --nav-position-right: 0;
  /* title */
  --nav-title-size: 0.75em;
  --nav-title-color: #6c757d;
  --nav-title-padding-x: 1rem;
  --nav-title-padding-y: 0.5em;
  /* nav button */
  --nav-button-padding-x: 1rem;
  --nav-button-padding-y: 0.5em;
  --nav-button-border-radius: 0.375em;
  --nav-button-font-size: 12px;
  --nav-button-hover-bg: #007bff;
  --nav-button-hover-text-color: #ffffff;
  --nav-button-distance: 0.75em;
  /* underline */
  --underline-border-width: 1px;
  --underline-border-color: #e9ecef;
  --underline-margin-y: 0.5em;
}

.custom-popup {
  display: inline-block;
  text-rendering: optimizeLegibility;
  position: relative;
}

.custom-popup input {
  display: none;
}

.custom-burger {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  background: var(--burger-bg);
  width: var(--burger-diameter);
  height: var(--burger-diameter);
  border-radius: var(--burger-btn-border-radius);
  border: none;
  cursor: pointer;
  overflow: hidden;
  transition: var(--burger-transition);
  outline: var(--burger-enable-outline-width) solid transparent;
  outline-offset: 0;
}

.custom-burger span {
  height: var(--burger-line-height);
  width: var(--burger-line-width);
  background: var(--burger-color);
  border-radius: var(--burger-line-border-radius);
  position: absolute;
  transition: var(--burger-line-transition);
}

.custom-burger span:nth-child(1) {
  top: var(--burger-offset);
}

.custom-burger span:nth-child(2) {
  bottom: var(--burger-offset);
}

.custom-burger span:nth-child(3) {
  top: 50%;
  transform: translateY(-50%);
}

.custom-popup-window {
  transform: scale(var(--nav-default-scale));
  visibility: hidden;
  opacity: 0;
  position: absolute;
  padding: var(--nav-padding-y) var(--nav-padding-x);
  background: var(--nav-bg);
  font-family: var(--nav-font-family);
  color: var(--nav-text-color);
  border-radius: var(--nav-border-radius);
  box-shadow: var(--nav-shadow-width) var(--nav-shadow-color);
  border: var(--nav-border-width) solid var(--nav-border-color);
  top: calc(var(--burger-diameter) + var(--burger-enable-outline-width) + var(--burger-enable-outline-offset));
  left: var(--nav-position-left);
  right: var(--nav-position-right);
  transition: var(--burger-transition);
  z-index: 999;
  min-width: 200px;
}

.custom-popup-window legend {
  padding: var(--nav-title-padding-y) var(--nav-title-padding-x);
  margin: 0;
  color: var(--nav-title-color);
  font-size: var(--nav-title-size);
  text-transform: uppercase;
  font-weight: 600;
}

.custom-popup-window ul {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

.custom-popup-window ul button {
  outline: none;
  width: 100%;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  color: #495057;
  font-size: var(--nav-button-font-size);
  padding: var(--nav-button-padding-y) var(--nav-button-padding-x);
  white-space: nowrap;
  border-radius: var(--nav-button-border-radius);
  cursor: pointer;
  column-gap: var(--nav-button-distance);
  transition: all 0.2s ease;
}

.custom-popup-window ul button i {
  color: var(--primary-color);
}

.custom-popup-window ul button i.fa-check {
  color: #28a745;
}

.custom-popup-window hr {
  margin: var(--underline-margin-y) 0;
  border: none;
  border-bottom: var(--underline-border-width) solid var(--underline-border-color);
}

.custom-popup-window ul button:hover,
.custom-popup-window ul button:focus-visible {
  background: rgba(0, 123, 255, 0.1);
  color: inherit;
}

.custom-burger:hover {
  transform: scale(var(--burger-hover-scale));
}

.custom-burger:active {
  transform: scale(var(--burger-active-scale));
}

.custom-burger:focus:not(:hover) {
  outline-color: var(--burger-enable-outline-color);
  outline-offset: var(--burger-enable-outline-offset);
}

.custom-popup.active .custom-burger span:nth-child(1) {
  top: 50%;
  transform: translateY(-50%) rotate(45deg);
}

.custom-popup.active .custom-burger span:nth-child(2) {
  bottom: 50%;
  transform: translateY(50%) rotate(-45deg);
}

.custom-popup.active .custom-burger span:nth-child(3) {
  transform: translateX(calc(var(--burger-diameter) * -1 - var(--burger-line-width)));
}

.custom-popup.active nav {
  transform: scale(var(--nav-active-scale));
  visibility: visible;
  opacity: 1;
}

.custom-popup-window ul button.disabled {
  cursor: not-allowed;
  opacity: 0.5;
  background: #f8f9fa;
}

::v-deep .custom-table-container .table-responsive {
  overflow: visible !important;
}

.m-h-90{
  min-height: 80vh;
  overflow: visible;
}

</style>
